import { useCallback, useRef, useState } from 'react';
import ReactGA from 'react-ga4';

import { msalInstance, scopes } from '@auth/authConfig';
import { useMessagesStore } from '@store/messagesStore';
import { SharedApiUrl } from '@services/endpoints';
import {
    Citation,
    MessageWithText,
    MessageWithTextAndCitations,
    ResponseType,
    Role,
} from '@services/types';
import { isAbortError } from '@utils/isAbortError';

export type StreamStatus = 'Error' | 'Ok';

type AllowedUrls = SharedApiUrl.ChatGeneral | SharedApiUrl.ChatAvatarGeneral;

export interface MessageToSend {
    prompt: string;
}

export interface MessageWithCitations {
    citations: Citation[];
    timestamp: string;
    role: Role.Tool;
    responseType: ResponseType.Citation;
}

export interface Chunk {
    streamStatus: StreamStatus;
    content: {
        chatResponse: MessageWithText | MessageWithCitations;
    };
}

const predefinedAnswers = {
    generating: 'Generating answer...',
    requestCancelled: 'You have decided to cancel this request.',
};

const errorMessages = {
    missingEnvVariableUrl: 'Missing environment variable: VITE_BASE_URL',
    noAccount: 'No account found',
    connectionError: 'Connection error or no stream available',
    unknown: 'An unknown error occurred',
};

const defaultLocation = 'Group';

export const useChat = (url: AllowedUrls) => {
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const controllerRef = useRef<AbortController | null>(null);
    const setMessages = useMessagesStore((state) => state.setMessages);
    const sessionId = useMessagesStore((state) => state.sessionId);

    const sendMessage = useCallback(
        async (
            messageContent: string,
            country: string = defaultLocation,
            detectedLanguage?: string
        ) => {
            if (controllerRef.current) {
                controllerRef.current.abort();
                setMessages((prevMessages) => {
                    const updated = [...prevMessages];
                    const last = updated[updated.length - 1];
                    if (last && last.role === Role.Assistant) {
                        updated[updated.length - 1] = {
                            ...last,
                            content: predefinedAnswers.requestCancelled,
                        };
                    }
                    return updated;
                });
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            setError(null);
            setIsPending(true);

            let apiMessages: MessageWithTextAndCitations[] = [];

            const newMessage: MessageWithText = {
                role: Role.User,
                content: messageContent,
                timestamp: new Date().toISOString(),
                responseType: ResponseType.Chat,
            };

            setMessages((prevMessages) => {
                apiMessages = [...prevMessages, newMessage];
                return [
                    ...apiMessages,
                    {
                        role: Role.Assistant,
                        content: predefinedAnswers.generating,
                        timestamp: new Date().toISOString(),
                        responseType: ResponseType.Chat,
                    },
                ];
            });

            try {
                const baseURL = import.meta.env.VITE_BASE_URL;

                if (!baseURL) {
                    throw new Error(errorMessages.missingEnvVariableUrl);
                }

                const account = msalInstance.getAllAccounts()[0];
                if (!account) {
                    throw new Error(errorMessages.noAccount);
                }
                const { accessToken } = await msalInstance.acquireTokenSilent({
                    scopes,
                    account,
                });

                const queryParams = new URLSearchParams();

                queryParams.set('sessionId', sessionId);
                queryParams.set('country', country);
                if (detectedLanguage) {
                    queryParams.set('locale', detectedLanguage);
                }

                const urlWithQueryParams = `${baseURL}${url}?${queryParams.toString()}`;

                if (url === SharedApiUrl.ChatGeneral) {
                    ReactGA.event({
                        category: 'Api Request',
                        action: 'Chat General',
                    });
                }
                if (url === SharedApiUrl.ChatAvatarGeneral) {
                    ReactGA.event({
                        category: 'Api Request',
                        action: 'Chat Avatar General',
                    });
                }
                const newMessage: MessageToSend = {
                    prompt: messageContent,
                };

                const response = await fetch(urlWithQueryParams, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(newMessage),
                    signal: controller.signal,
                });

                if (!response.ok || !response.body) {
                    throw new Error(errorMessages.connectionError);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder('utf-8');
                let buffer = '';

                let doneReading = false;

                while (!doneReading) {
                    const { value, done } = await reader.read();
                    doneReading = done;

                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (!line.trim()) continue;
                        const parsed: Chunk = JSON.parse(line);
                        const { chatResponse } = parsed.content;
                        setMessages((prevMessages) => {
                            const updatedMessages = [...prevMessages];
                            const indexToUpdate = updatedMessages.findLastIndex(
                                (message) => message.role === Role.Assistant
                            );
                            const last = updatedMessages[indexToUpdate];

                            const timestamp = chatResponse.timestamp;

                            if (chatResponse.responseType === ResponseType.Chat) {
                                if (chatResponse.role === Role.Assistant) {
                                    updatedMessages[indexToUpdate] = {
                                        ...last,
                                        content:
                                            `${last.content === predefinedAnswers.generating ? '' : last.content}` +
                                            chatResponse.content,
                                        timestamp,
                                    };
                                } else {
                                    updatedMessages.push(chatResponse);
                                }
                            } else if (chatResponse.responseType === ResponseType.Citation) {
                                updatedMessages[indexToUpdate] = {
                                    ...last,
                                    citations: chatResponse.citations,
                                    timestamp,
                                };
                            }

                            return updatedMessages;
                        });
                    }
                }
            } catch (error) {
                const shouldHandleError = !isAbortError(error);

                if (shouldHandleError) {
                    if (error instanceof Error) {
                        setError(error);
                    } else {
                        setError(new Error(errorMessages.unknown));
                    }
                }
            } finally {
                if (controllerRef.current === controller) {
                    setIsPending(false);
                    controllerRef.current = null;

                    // Normalize citations after streaming is complete
                    setMessages((prevMessages) => {
                        const updated = [...prevMessages];
                        const last = updated[updated.length - 1];
                        if (last && last.role === Role.Assistant && last.citations) {
                            updated[updated.length - 1] = normalizeCitations(last);
                        }
                        return updated;
                    });
                }
            }
        },
        [url, setMessages, sessionId]
    );

    const normalizeCitations = (message: MessageWithTextAndCitations) => {
        if (message.role !== Role.Assistant || !message.citations) {
            return message;
        }

        let normalizedContent = message.content;
        for (const citation of message.citations) {
            const escapedReference = citation.citationReference.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&'
            );
            const regex = new RegExp(escapedReference, 'g');
            normalizedContent = normalizedContent.replace(regex, '[' + citation.index + ']');
        }

        return {
            ...message,
            content: normalizedContent,
        };
    };

    const stopStreaming = useCallback(() => {
        if (!controllerRef.current) return;

        controllerRef.current.abort();
        controllerRef.current = null;

        setIsPending(false);
        setError(null);
        setMessages((prevMessages) => {
            const updated = [...prevMessages];
            const last = updated[updated.length - 1];
            if (last && last.role === Role.Assistant) {
                updated[updated.length - 1] = {
                    ...last,
                    content: predefinedAnswers.requestCancelled,
                };
            }
            return updated;
        });
    }, [setMessages]);

    return { isPending, error, sendMessage, stopStreaming };
};
